import { projectId, publicAnonKey } from "/utils/supabase/info";

const REQUEST_TIMEOUT_MS = 12000;
const REQUEST_RETRY_DELAY_MS = 350;
const RETRYABLE_STATUS = new Set([408, 429, 500, 502, 503, 504]);
const BASE_URL = `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4`;

async function delay(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithTimeout(input: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

async function parseJsonResponse(response: Response): Promise<any> {
  const text = await response.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function toErrorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;
  if (error instanceof Error) return error.message || fallback;
  if (typeof error === "string") return error;
  return fallback;
}

function buildHeaders(accessToken: string | null, includeJson = true): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${publicAnonKey}`,
  };
  if (includeJson) headers["Content-Type"] = "application/json";
  if (accessToken) headers["X-User-Token"] = accessToken;
  return headers;
}

async function request(
  accessToken: string | null,
  endpoint: string,
  options: RequestInit = {},
): Promise<any> {
  const url = `${BASE_URL}${endpoint}`;
  let response: Response | null = null;
  let lastError: unknown = null;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      response = await fetchWithTimeout(url, {
        ...options,
        headers: {
          ...buildHeaders(accessToken, options.body !== undefined),
          ...(options.headers || {}),
        },
      });

      if (response.ok) {
        return await parseJsonResponse(response);
      }

      const parsed = await parseJsonResponse(response);
      const message =
        (parsed && typeof parsed.error === "string" && parsed.error) ||
        (parsed && typeof parsed.message === "string" && parsed.message) ||
        `${response.status} ${response.statusText}`;
      lastError = new Error(message);
      if (!RETRYABLE_STATUS.has(response.status) || attempt === 1) {
        throw lastError;
      }
    } catch (error) {
      lastError = error;
      if (attempt === 1) throw error;
    }

    await delay(REQUEST_RETRY_DELAY_MS);
  }

  throw new Error(toErrorMessage(lastError, "Social request failed"));
}

export async function getSocialOverview(accessToken: string | null): Promise<any> {
  return request(accessToken, "/community/social/overview");
}

export async function getSocialFeed(
  accessToken: string | null,
  mode: "following" | "discover",
  limit = 20,
): Promise<any> {
  return request(
    accessToken,
    `/community/social/feed?mode=${encodeURIComponent(mode)}&limit=${encodeURIComponent(String(limit))}`,
  );
}

export async function createSocialPost(accessToken: string | null, payload: {
  content: string;
  tags: Array<string>;
}): Promise<any> {
  return request(accessToken, "/community/social/posts", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function reactToSocialPost(
  accessToken: string | null,
  postId: string,
  reactionType: "like" | "insight" | "support",
): Promise<any> {
  return request(accessToken, `/community/social/posts/${encodeURIComponent(postId)}/react`, {
    method: "POST",
    body: JSON.stringify({ reactionType }),
  });
}

export async function commentOnSocialPost(
  accessToken: string | null,
  postId: string,
  content: string,
): Promise<any> {
  return request(accessToken, `/community/social/posts/${encodeURIComponent(postId)}/comment`, {
    method: "POST",
    body: JSON.stringify({ content }),
  });
}

export async function getSocialSuggestions(accessToken: string | null, limit = 8): Promise<any> {
  return request(accessToken, `/community/social/suggestions?limit=${encodeURIComponent(String(limit))}`);
}

export async function searchSocialProfiles(accessToken: string | null, search: string): Promise<any> {
  const query = encodeURIComponent(search.trim());
  return request(accessToken, `/community/social/profiles?search=${query}`);
}

export async function getSocialMutuals(accessToken: string | null, targetUserId: string): Promise<any> {
  return request(accessToken, `/community/social/mutuals/${encodeURIComponent(targetUserId)}`);
}

export async function toggleSocialFollow(accessToken: string | null, targetUserId: string): Promise<any> {
  return request(accessToken, `/community/social/follow/${encodeURIComponent(targetUserId)}`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function getSocialNotifications(accessToken: string | null, limit = 30): Promise<any> {
  return request(accessToken, `/community/social/notifications?limit=${encodeURIComponent(String(limit))}`);
}

export async function markSocialNotificationRead(accessToken: string | null, notificationId: string): Promise<any> {
  return request(
    accessToken,
    `/community/social/notifications/${encodeURIComponent(notificationId)}/read`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function markAllSocialNotificationsRead(accessToken: string | null): Promise<any> {
  return request(accessToken, "/community/social/notifications/read-all", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function getSocialDigestLatest(accessToken: string | null): Promise<any> {
  return request(accessToken, "/community/social/digest/latest");
}

export async function ensureFreshSocialDigest(accessToken: string | null, force = false): Promise<any> {
  return request(accessToken, "/community/social/digest/ensure-fresh", {
    method: "POST",
    body: JSON.stringify({ force }),
  });
}

export async function getSocialProfile(accessToken: string | null): Promise<any> {
  return request(accessToken, "/community/social/profile");
}

export async function updateSocialProfile(accessToken: string | null, payload: {
  displayName: string;
  headline: string;
  bio: string;
  interests: Array<string>;
}): Promise<any> {
  return request(accessToken, "/community/social/profile", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function getSocialMetrics(accessToken: string | null, days = 14): Promise<any> {
  return request(accessToken, `/community/social/metrics?days=${encodeURIComponent(String(days))}`);
}

export async function getSocialSlaRuntimeStatus(accessToken: string | null): Promise<any> {
  return request(accessToken, "/community/social/sla/runtime-status");
}

export async function getSocialIntelligenceConfig(accessToken: string | null): Promise<any> {
  return request(accessToken, "/community/social/intelligence/config");
}

export async function updateSocialIntelligenceConfig(
  accessToken: string | null,
  payload: Record<string, unknown>,
): Promise<any> {
  return request(accessToken, "/community/social/intelligence/config", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function getSocialGraphSummary(accessToken: string | null): Promise<any> {
  return request(accessToken, "/community/social/intelligence/graph-summary");
}

export async function getSocialIntelligenceRecommendations(
  accessToken: string | null,
  query = "",
  limit = 10,
): Promise<any> {
  const params = new URLSearchParams();
  if (query.trim()) params.set("query", query.trim());
  params.set("limit", String(limit));
  return request(accessToken, `/community/social/intelligence/recommendations?${params.toString()}`);
}

export async function getSocialIntelligenceAutoPolicyConfig(accessToken: string | null): Promise<any> {
  return request(accessToken, "/community/social/intelligence/auto-policy/config");
}

export async function updateSocialIntelligenceAutoPolicyConfig(
  accessToken: string | null,
  payload: Record<string, unknown>,
): Promise<any> {
  return request(accessToken, "/community/social/intelligence/auto-policy/config", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function runSocialIntelligenceAutoPolicy(accessToken: string | null): Promise<any> {
  return request(accessToken, "/community/social/intelligence/auto-policy/run", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function getSocialIntelligenceAutoPolicyAudit(
  accessToken: string | null,
  limit = 30,
): Promise<any> {
  return request(
    accessToken,
    `/community/social/intelligence/auto-policy/audit?limit=${encodeURIComponent(String(limit))}`,
  );
}

export async function getSocialIntelligenceAutoPolicyHealthReport(
  accessToken: string | null,
  days = 7,
): Promise<any> {
  return request(
    accessToken,
    `/community/social/intelligence/auto-policy/health-report?days=${encodeURIComponent(String(days))}`,
  );
}
